(ns ductile.ui.load-builder-v2.selectors
  (:require [clojure.string :as str]
            [ductile.ui.components.columns :as columns]
            [ductile.ui.components.filters :as filters]))

(defn load-key [load]
  (or (:load/id load)
      (:ductile/id load)))

(defn vehicles-by-id [fahrzeuge]
  (into {} (map (juxt :ductile/id identity) fahrzeuge)))

(defn loads->assignment [loads fahrzeuge]
  (let [grouped-fahrzeuge (->> fahrzeuge
                               (filter :load/id)
                               (group-by :load/id))]
    (into {}
          (map (fn [load]
                 [(load-key load)
                  (mapv :ductile/id (get grouped-fahrzeuge (load-key load)))]))
          loads)))

(defn assignments->assigned-ids [transport->vehicle-ids]
  (into #{} (mapcat identity (vals transport->vehicle-ids))))

(defn vehicle-source-transport-id [transport->vehicle-ids vehicle-id]
  (first (keep (fn [[transport-id vehicle-ids]]
                 (when (some #{vehicle-id} vehicle-ids)
                   transport-id))
               transport->vehicle-ids)))

(defn waypoint-key [wp]
  (or (some-> (:ductile/id wp) str)
      [(:address/postcode wp)
       (:address/city wp)
       (:location/longitude wp)
       (:location/latitude wp)]))

(defn- normalize-waypoint-order [waypoint-order]
  (when (seq waypoint-order)
    (mapv str waypoint-order)))

(defn- waypoint-coord [{:location/keys [longitude latitude]}]
  (when (and (number? longitude) (number? latitude))
    [longitude latitude]))

(defn- distance-squared [[origin-lon origin-lat] [lon lat]]
  (+ (js/Math.pow (- lon origin-lon) 2)
     (js/Math.pow (- lat origin-lat) 2)))

(defn- sort-waypoints-by-origin-distance [origin-coord waypoints]
  (if (and (number? (first origin-coord))
           (number? (second origin-coord)))
    (->> waypoints
         (sort-by #(let [coord (waypoint-coord %)]
                     (if coord
                       (distance-squared origin-coord coord)
                       js/Number.POSITIVE_INFINITY)))
         vec)
    (vec waypoints)))

(defn order-waypoints [waypoints waypoint-order]
  (if-let [waypoint-order (normalize-waypoint-order waypoint-order)]
    (let [rank (zipmap waypoint-order (range))
          tail-rank (count waypoint-order)]
      (->> waypoints
           (map-indexed vector)
           (sort-by (fn [[idx wp]]
                      [(get rank (waypoint-key wp) tail-rank) idx]))
           (mapv second)))
    (vec waypoints)))

(defn transport-persisted-waypoint-order [transport-id transport->vehicle-ids vehicle-id->transport]
  (let [first-vehicle (some-> (get transport->vehicle-ids transport-id) first vehicle-id->transport)
        order (or (get-in first-vehicle [:transport/transport :ductile/data :destination/ductile-ids])
                  (get-in first-vehicle [:transport :ductile/data :destination/ductile-ids])
                  (get-in first-vehicle [:ductile/data :destination/ductile-ids]))]
    (normalize-waypoint-order order)))

(defn transport-persisted-waypoint-order-from-transport [transport]
  (normalize-waypoint-order
   (or (get-in transport [:ductile/data :destination/ductile-ids])
       (get-in transport [:transport/transport :ductile/data :destination/ductile-ids])
       (get-in transport [:transport :ductile/data :destination/ductile-ids]))))

(defn transport-optimized-route?
  ([transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings]
   (transport-optimized-route? transport-id
                               transport->vehicle-ids
                               vehicle-id->transport
                               transport->waypoint-settings
                               nil))
  ([transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings persisted-waypoint-order]
   (let [settings (get transport->waypoint-settings transport-id)]
     (if (contains? settings :optimized-route?)
       (not= false (:optimized-route? settings))
       true))))

(defn transport-waypoints
  ([transport-id transport->vehicle-ids vehicle-id->transport]
   (transport-waypoints transport-id transport->vehicle-ids vehicle-id->transport nil nil))
  ([transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings]
   (transport-waypoints transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings nil))
  ([transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings persisted-waypoint-order]
   (let [base-waypoints
         (->> (get transport->vehicle-ids transport-id [])
              (keep vehicle-id->transport)
              (map :transport/destination)
              (remove nil?)
              (reduce (fn [{:keys [seen ordered]} wp]
                        (let [k (waypoint-key wp)]
                          (if (contains? seen k)
                            {:seen seen :ordered ordered}
                            {:seen (conj seen k)
                             :ordered (conj ordered wp)})))
                      {:seen #{} :ordered []})
              :ordered)
         optimized-route? (transport-optimized-route? transport-id
                                                      transport->vehicle-ids
                                                      vehicle-id->transport
                                                      transport->waypoint-settings
                                                      persisted-waypoint-order)
         explicit-waypoint-order (normalize-waypoint-order
                                  (get-in transport->waypoint-settings [transport-id :waypoint-order]))
         fallback-waypoint-order (when-not optimized-route?
                                   (or (normalize-waypoint-order persisted-waypoint-order)
                                       (transport-persisted-waypoint-order transport-id
                                                                           transport->vehicle-ids
                                                                           vehicle-id->transport)))
         waypoint-order (or explicit-waypoint-order fallback-waypoint-order)
         origin-coord (some-> (get transport->vehicle-ids transport-id)
                              first
                              vehicle-id->transport
                              :transport/origin
                              waypoint-coord)]
     (if waypoint-order
       (order-waypoints base-waypoints waypoint-order)
       (sort-waypoints-by-origin-distance origin-coord base-waypoints)))))

(defn ordered-draft-ids [draft-ids draft-order]
  (let [present? (set draft-ids)
        ranked (filter present? (reverse (or draft-order [])))
        missing (remove (set ranked) draft-ids)]
    (vec (concat ranked missing))))

(defn ordered-transport-ids [transport-ids ordered-draft-ids]
  (let [draft-set (set ordered-draft-ids)
        persisted (remove draft-set transport-ids)]
    (vec (concat ordered-draft-ids persisted))))

(defn draft-index-by-id [ordered-draft-ids]
  (into {} (map-indexed (fn [i id] [id (inc i)]) ordered-draft-ids)))

(defn transport-summary
  ([transport-id transport->vehicle-ids vehicle-id->transport]
   (transport-summary transport-id transport->vehicle-ids vehicle-id->transport nil nil))
  ([transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings]
   (transport-summary transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings nil))
  ([transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings persisted-waypoint-order]
   (let [vehicle-ids (get transport->vehicle-ids transport-id [])
         first-vehicle (get vehicle-id->transport (first vehicle-ids))]
     {:transport-id transport-id
      :vehicle-ids vehicle-ids
      :vehicle-id-set (set vehicle-ids)
      :vehicle-count (count vehicle-ids)
      :load-id (:load/id first-vehicle)
      :carrier-label (or (get-in first-vehicle [:transport/carrier :address/name])
                         (get-in first-vehicle [:transport/transporter :transport/carrier :address/name])
                         (get-in first-vehicle [:transport/transporter :truck/number]))
      :waypoints (transport-waypoints transport-id
                                      transport->vehicle-ids
                                      vehicle-id->transport
                                      transport->waypoint-settings
                                      persisted-waypoint-order)})))

(defn transport-summaries
  ([transport-ids transport->vehicle-ids vehicle-id->transport]
   (transport-summaries transport-ids transport->vehicle-ids vehicle-id->transport nil nil))
  ([transport-ids transport->vehicle-ids vehicle-id->transport transport->waypoint-settings]
   (transport-summaries transport-ids transport->vehicle-ids vehicle-id->transport transport->waypoint-settings nil))
  ([transport-ids transport->vehicle-ids vehicle-id->transport transport->waypoint-settings transport-id->persisted-waypoint-order]
   (into {}
         (map (fn [transport-id]
                [transport-id (transport-summary transport-id
                                                 transport->vehicle-ids
                                                 vehicle-id->transport
                                                 transport->waypoint-settings
                                                 (get transport-id->persisted-waypoint-order transport-id))]))
         transport-ids)))

(defn normalize-view-name [name]
  (some-> name str str/trim not-empty))

(defn- normalized-selected-cols [col-settings]
  (->> (columns/selected-profile (columns/from-localstorage col-settings))
       :cols
       (mapv (fn [{:keys [attr sticky?]}]
               {:attr attr
                :sticky? (boolean sticky?)}))))

(defn- filter-signature [f]
  {::filters/field (::filters/field f)
   ::filters/operator (::filters/operator f)
   ::filters/value (::filters/value f)})

(defn- normalized-filters [serialized-filters]
  (->> (filters/deserialize-filters serialized-filters)
       (map filter-signature)
       set))

(defn snapshot-equal? [a b]
  (= {:cols (normalized-selected-cols (:col-settings a))
      :filters (normalized-filters (:filters a))}
     {:cols (normalized-selected-cols (:col-settings b))
      :filters (normalized-filters (:filters b))}))

(defn current-view [views selected-view-id]
  (first (filter #(= selected-view-id (:id %)) views)))

(defn name-available?
  ([views name]
   (name-available? views name nil))
  ([views name ignore-id]
   (let [candidate (some-> (normalize-view-name name) str/lower-case)]
     (and candidate
          (not-any? (fn [{:keys [id name]}]
                      (and (not= id ignore-id)
                           (= candidate (some-> (normalize-view-name name) str/lower-case))))
                    views)))))
